import { html, cx, useState, useEffect, useRef, useCallback } from '../h.js'
import { Icon } from '../icons.js'
import { t, LANGUAGES, setLanguage } from '../i18n.js'
import { copyText, shortId, listTime } from '../format.js'
import { Avatar, Switch, SettingRow, CopyField, Badges, useMenu, useToast } from '../components.js'
import { useStore, applyTheme } from '../store.js'
import { normalizeUsername, usernameError, nameError } from '@pine/core'
import { appVersion, buildDate } from '../version.js'
import { canRememberKey, rememberKey } from '../keysave.js'

/**
 * Settings, laid out the way phones do it: who you are at the top, then a row
 * for each group, and each group on a page of its own.
 *
 * It used to be one long page with every switch in the app one under the
 * other — profile, theme, notifications, privacy, calls, devices, keys — and
 * finding one meant scrolling past all the rest. Which page is open is kept
 * in the store (settingsPage): the Settings tab always comes back to the top,
 * and back — Android's button, the browser's — leaves a page before it leaves
 * Settings.
 */
export function Settings () {
  const store = useStore()
  const page = store.get('settingsPage') || 'home'
  const pages = settingsPages()
  const current = pages.find((p) => p.id === page) || null
  const open = (next) => store.set({ settingsPage: next })

  useEffect(() => {
    if (!current) return
    return store.pushBack(() => {
      store.set({ settingsPage: 'home' })
      return true
    })
  }, [current && current.id, store])

  if (current) {
    return html`
      <aside class="pane settings" style="width:100%;max-width:520px">
        <header class="pane-head">
          <button class="icon-btn" aria-label=${t('onboard.back')} onClick=${() => open('home')}>${Icon.back(20)}</button>
          <h1 class="grow">${current.title}</h1>
        </header>
        <div class="pane-body"><${current.Page} store=${store} /></div>
      </aside>`
  }

  return html`
    <aside class="pane settings" style="width:100%;max-width:520px">
      <header class="pane-head"><h1 class="grow">${t('settings.title')}</h1></header>
      <div class="pane-body">
        <${SettingsHome} store=${store} pages=${pages} open=${open} />
      </div>
    </aside>`
}

/**
 * Every page there is, in the order the home page lists them.
 *
 * There used to be ten, and most held two or three switches: notifications,
 * calls and running in the background were three pages about one question
 * (will a call reach me?), theme and language a page apart from the chat
 * switches, the recovery key and the device list each a page of one block.
 * Finding a setting meant guessing which of ten. Now there are four, each a
 * question somebody actually asks, and the sections inside say the rest.
 */
function settingsPages () {
  return [
    { id: 'profile', group: 0, title: t('settings.profile'), icon: Icon.contacts, tint: '#3a8ef0', Page: ProfilePage },
    { id: 'notifications', group: 1, title: t('settings.notificationsCalls'), icon: Icon.bell, tint: '#ef5b5b', Page: NotificationsPage },
    { id: 'chats', group: 1, title: t('settings.chatsAppearance'), icon: Icon.chat, tint: '#2f9e6b', Page: ChatsPage },
    { id: 'privacy', group: 1, title: t('settings.privacySecurity'), icon: Icon.shield, tint: '#5f7ea3', Page: PrivacyPage },
    { id: 'pro', group: 3, title: 'Pine AI Pro', icon: Icon.crown, tint: '#d99a1e', Page: ProPage },
    { id: 'about', group: 2, title: t('settings.about'), icon: Icon.pine, tint: 'var(--accent)', Page: AboutPage }
  ]
}

/** Something this phone does not allow that a call needs (see BackgroundSection). */
function backgroundAttention (platform) {
  const background = platform.background
  if (!background || !background.available) return false
  const status = background.status()
  if (!status) return false
  return ['notifications', 'battery', 'unrestricted', 'fullScreen'].some((key) => status[key] === false)
}

function SettingsHome ({ store, pages, open }) {
  const { client } = store
  const profile = client.identity.profile
  const username = profile.username ? '@' + profile.username : shortId(client.identity.id)
  const main = pages.filter((p) => p.group === 1)
  const about = pages.find((p) => p.id === 'about')
  const pro = pages.find((p) => p.id === 'pro')
  const plan = useProPlan(store)
  const theme = { system: t('settings.themeSystem'), light: t('settings.themeLight'), dark: t('settings.themeDark') }
  const language = (LANGUAGES.find((l) => l.code === client.settings.language) || {}).label || ''
  const attention = backgroundAttention(store.platform)
  const sub = {
    notifications: attention ? t('settings.bgAttention') : '',
    chats: [theme[client.settings.theme] || '', language].filter(Boolean).join(' · '),
    about: 'Pine ' + appVersion() + (buildDate() ? ' · сборка ' + buildDate() : '')
  }

  return html`
    <button class="settings-me" onClick=${() => open('profile')}>
      <${Avatar} name=${profile.name} id=${client.identity.id} src=${profile.avatar || null} size="lg" />
      <span class="body">
        <span class="name">${profile.name || shortId(client.identity.id)}</span>
        <span class="sub">${username}</span>
      </span>
      <span class="chev">${Icon.forward(18)}</span>
    </button>

    <div class="settings-group">
      ${main.map((p) => html`
        <${NavRow} key=${p.id} icon=${p.icon} tint=${p.tint} title=${p.title} sub=${sub[p.id]}
          warn=${p.id === 'notifications' && attention} onClick=${() => open(p.id)} />`)}
    </div>

    ${store.client.assistant && store.client.assistant.url && html`
      <div class="settings-group">
        <${NavRow} icon=${pro.icon} tint=${pro.tint} title=${pro.title}
          sub=${plan && plan.pro ? t('pro.active', { date: longDate(plan.proUntil) }) : t('pro.home')}
          onClick=${() => open('pro')} />
      </div>`}

    <div class="settings-group">
      <${NavRow} icon=${Icon.mail} tint="#5b8def" title=${t('settings.contactDev')}
        sub=${t('settings.contactDevHint')}
        onClick=${() => store.showDialog({ type: 'contact-dev' })} />
      <${NavRow} icon=${about.icon} tint=${about.tint} title=${about.title} sub=${sub.about}
        onClick=${() => open('about')} />
    </div>
    <div style="height:24px"></div>`
}

/** A row on the home page: a coloured tile, a title, and the way in. */
function NavRow ({ icon, tint, title, sub = '', warn = false, onClick = null }) {
  const Tag = onClick ? 'button' : 'div'
  return html`
    <${Tag} class="list-row nav-row" onClick=${onClick}>
      <span class="tile" style=${{ background: tint }}>${icon(17)}</span>
      <span class="body">
        <span class="title">${title}</span>
        ${sub && html`<span class="sub" style=${warn ? 'color:var(--danger)' : ''}>${sub}</span>`}
      </span>
      ${onClick && html`<span class="chev">${Icon.forward(16)}</span>`}
    <//>`
}

/** A titled block inside a page. */
function Section ({ title, children }) {
  return html`
    <div class="section-title">${title}</div>
    <div class="settings-group">${children}</div>`
}

/** Changing a setting, and whatever has to follow it at once. */
function useUpdate (store) {
  return (patch) => {
    store.client.setSettings(patch)
    if (patch.theme) applyTheme(patch.theme)
    if (patch.language) setLanguage(patch.language)
  }
}

function ProfilePage ({ store }) {
  const { client } = store
  const toast = useToast()
  const [name, setName] = useState(client.identity.profile.name || '')
  const [bio, setBio] = useState(client.identity.profile.bio || '')
  const [dirty, setDirty] = useState(false)

  // The profile can change under this screen — taken from the account's
  // newer copy a few seconds after launch — and these fields were seeded once
  // and never again, so a restored name and bio could be on the device and
  // still show as empty boxes here. Follow it, unless somebody is typing.
  useEffect(() => {
    return client.on('profile', (profile) => {
      if (dirty) return
      setName(profile.name || '')
      setBio(profile.bio || '')
    })
  }, [client, dirty])

  // Pictographs are not allowed in a display name — see lib/names.js in the
  // core for what that is about. Caught here so the person is told, rather
  // than having the emoji quietly vanish out of what they typed.
  const nameProblem = dirty ? nameError(name) : null

  async function saveProfile () {
    if (nameError(name)) return
    await client.updateProfile({ name: name.trim(), bio: bio.trim() })
    setDirty(false)
    toast(t('settings.saved'))
  }

  return html`
    <div class="panel-hero">
      <${AvatarPicker} client=${client} name=${name} />
    </div>
    <div class="settings-group col gap3" style="padding:12px 16px 16px">
      <div class="field">
        <label>${t('settings.name')}</label>
        <input class=${cx('input', nameProblem === 'emoji' && 'invalid')} value=${name} maxlength="64"
               onInput=${(e) => { setName(e.target.value); setDirty(true) }} />
        ${nameProblem === 'emoji' && html`<span class="error">${t('name.err.emoji')}</span>`}
      </div>
      <div class="field">
        <label>${t('settings.bio')}</label>
        <input class="input" value=${bio} maxlength="280"
               onInput=${(e) => { setBio(e.target.value); setDirty(true) }} />
      </div>
      ${dirty && html`
        <button class="btn btn-primary" disabled=${!!nameProblem}
                onClick=${saveProfile}>${t('settings.save')}</button>`}
      <${MyBadges} client=${client} />
      <${UsernameField} client=${client} />
      <${CopyField} label=${t('settings.yourId')} value=${client.identity.id} />
      <div class="hint mute small">${t('settings.idHint')}</div>
    </div>`
}

/**
 * Will a message or a call reach me: notifications, the ringtone and the
 * phone's own call screen, and on Android everything that lets Pine keep
 * running in the background.
 */
function NotificationsPage ({ store }) {
  const { platform } = store
  const settings = store.client.settings
  const update = useUpdate(store)
  const calls = (platform.ringtone && platform.ringtone.available) || (platform.telecom && platform.telecom.available)
  return html`
    <${Section} title=${t('settings.notificationsGroup')}>
      <${SettingRow} icon=${Icon.bell(18)} title=${t('settings.notificationsOn')}
        right=${html`<${Switch} checked=${settings.notifications}
          onChange=${async (v) => {
            if (v && platform.notifications) {
              await platform.notifications.enable()
            } else if (v && typeof Notification !== 'undefined' && Notification.permission === 'default') {
              await Notification.requestPermission()
            }
            update({ notifications: v })
          }} />`} />
      ${platform.notifications && html`<${BrowserNotificationsRow} store=${store} />`}
      <${SettingRow} icon=${Icon.volume(18)} title=${t('settings.sound')}
        right=${html`<${Switch} checked=${settings.notificationSound}
          onChange=${(v) => update({ notificationSound: v })} />`} />
      ${platform.autostart && html`<${AutostartRow} store=${store} />`}
    <//>
    ${calls && html`
      <${Section} title=${t('settings.calls')}>
        ${platform.ringtone && platform.ringtone.available && html`<${RingtoneRow} store=${store} />`}
        <${TelecomRow} store=${store} />
        ${platform.push && html`
          <${SettingRow} icon=${Icon.phone(18)} title=${t('settings.ringFromPush')} sub=${t('settings.ringFromPushHint')}
            right=${html`<${Switch} checked=${settings.ringFromPush !== false}
              onChange=${(on) => {
                update({ ringFromPush: on })
                // Registered again, so the server knows what to put in a call's push.
                const token = platform.push.token()
                if (token) store.client.setPushToken(token).catch(() => {})
              }} />`} />`}
      <//>`}
    ${platform.background && platform.background.available && html`
      <${Section} title=${t('settings.background')}>
        <${BackgroundSection} store=${store} />
      <//>`}
    <div style="height:16px"></div>`
}

/** How chats look and behave: theme and language first, then the switches. */
function ChatsPage ({ store }) {
  const settings = store.client.settings
  const update = useUpdate(store)
  return html`
    <div class="section-title">${t('settings.theme')}</div>
    <div style="padding:0 16px 12px">
      <div class="row gap2">
        ${[['system', Icon.monitor(17), t('settings.themeSystem')],
           ['light', Icon.sun(17), t('settings.themeLight')],
           ['dark', Icon.moon(17), t('settings.themeDark')]].map(([value, icon, label]) => html`
          <button key=${value} class=${cx('btn', settings.theme === value ? 'btn-primary' : 'btn-soft')}
                  style="flex:1" onClick=${() => update({ theme: value })}>
            ${icon}<span class="small">${label}</span>
          </button>`)}
      </div>
    </div>
    <div class="settings-group">
      <${SettingRow} icon=${Icon.globe(18)} title=${t('settings.language')}
        right=${html`
          <select class="select" style="width:auto" value=${settings.language}
                  onChange=${(e) => update({ language: e.target.value })}>
            ${LANGUAGES.map((l) => html`<option key=${l.code} value=${l.code}>${l.label}</option>`)}
          </select>`} />
    </div>
    <${Section} title=${t('settings.chatsGroup')}>
      <${SettingRow} icon=${Icon.send(18)} title=${t('settings.enterToSend')}
        right=${html`<${Switch} checked=${settings.enterToSend}
          onChange=${(v) => update({ enterToSend: v })} />`} />
      <${SettingRow} icon=${Icon.download(18)} title=${t('settings.autoDownload')}
        right=${html`<${Switch} checked=${settings.autoDownloadMedia}
          onChange=${(v) => update({ autoDownloadMedia: v })} />`} />
      <${SettingRow} icon=${Icon.link(18)} title=${t('settings.linkPreviews')}
        sub=${t('settings.linkPreviewsHint')}
        right=${html`<${Switch} checked=${settings.linkPreviews !== false}
          onChange=${(v) => update({ linkPreviews: v })} />`} />
    <//>
    <div style="height:16px"></div>`
}

/**
 * What others can see, and what holds the account: receipts and typing, the
 * recovery key, the devices signed in, and signing out.
 */
function PrivacyPage ({ store }) {
  const { client } = store
  const settings = client.settings
  const update = useUpdate(store)
  const toast = useToast()
  const [showKey, setShowKey] = useState(false)
  return html`
    <${Section} title=${t('settings.privacy')}>
      <${SettingRow} icon=${Icon.checkDouble(18)} title=${t('settings.readReceipts')}
        right=${html`<${Switch} checked=${settings.readReceipts}
          onChange=${(v) => update({ readReceipts: v })} />`} />
      <${SettingRow} icon=${Icon.edit(18)} title=${t('settings.typingIndicators')}
        right=${html`<${Switch} checked=${settings.typingIndicators}
          onChange=${(v) => update({ typingIndicators: v })} />`} />
      <${SettingRow} icon=${Icon.database(18)} title=${t('settings.backupKeys')}
        sub=${t('settings.backupKeysHint')}
        right=${html`<${Switch} checked=${settings.backupKeys !== false}
          onChange=${(v) => update({ backupKeys: v })} />`} />
    <//>
    <${Section} title=${t('settings.recoveryKey')}>
      <${SettingRow} icon=${Icon.key(18)} title=${t('settings.recoveryKey')}
        sub=${showKey ? '' : '••••-••••-••••-••••'}
        onClick=${() => setShowKey((v) => !v)}
        right=${showKey ? Icon.eyeOff(17) : Icon.eye(17)} />
      ${showKey && html`
        <div style="padding:0 16px 12px" class="col gap2">
          <div class="recovery-key selectable">${client.identity.recoveryKey}</div>
          <button class="btn btn-soft" onClick=${async () => {
            await copyText(client.identity.recoveryKey)
            toast(t('action.copied'))
          }}>${Icon.copy(16)} ${t('action.copy')}</button>
          ${canRememberKey(store.platform) && html`
            <button class="btn btn-soft" onClick=${async () => {
              try {
                await rememberKey({
                  key: client.identity.recoveryKey,
                  name: client.identity.profile.name,
                  account: client.identity.id
                })
              } catch {
                toast(t('err.generic'), { error: true })
              }
            }}>${Icon.key(16)} ${t('keysave.save')}</button>`}
        </div>`}
    <//>
    <${Section} title=${t('devices.title')}>
      <${DevicesSection} store=${store} />
    <//>
    <div class="settings-group">
      <${SettingRow} icon=${Icon.logout(18)} title=${t('settings.signOut')}
        sub=${t('settings.signOutHint')} danger
        onClick=${() => store.showDialog({ type: 'signout' })} />
    </div>
    <div style="height:16px"></div>`
}

/** This account's Pine AI Pro, as the server last said: undefined while asking. */
function useProPlan (store) {
  const assistant = store.client.assistant
  const [plan, setPlan] = useState(undefined)
  useEffect(() => {
    if (!assistant || !assistant.url) return
    let live = true
    assistant.plan().then((answer) => { if (live) setPlan(answer) }).catch(() => { if (live) setPlan(null) })
    return () => { live = false }
  }, [assistant])
  return plan
}

function longDate (ms) {
  try {
    return new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(ms))
  } catch {
    return new Date(ms).toLocaleDateString()
  }
}

/**
 * Pine AI Pro: a subscription for Anya, with more models and higher limits.
 * It is arranged with the developer by mail, not paid for in the app: this
 * page says what it gives, whether this account has it and until when, and
 * opens a letter that already carries the Pine ID. The operator grants it
 * in the console, and the server answers for it (/api/assistant/access).
 */
function ProPage ({ store }) {
  const assistant = store.client.assistant
  const plan = useProPlan(store)
  const [plans, setPlans] = useState(null)
  useEffect(() => {
    let live = true
    assistant.plans().then((answer) => { if (live) setPlans(answer) }).catch(() => {})
    return () => { live = false }
  }, [assistant])

  const active = !!(plan && plan.pro)
  const status = plan === undefined
    ? '…'
    : plan === null
      ? t('pro.unavailable')
      : active
        ? t('pro.active', { date: longDate(plan.proUntil) })
        : plan.proUntil ? t('pro.ended', { date: longDate(plan.proUntil) }) : t('pro.inactive')
  const limits = plans && plans.limits
  const rows = limits
    ? [['perDay', t('pro.perDay')], ['perMonth', t('pro.perMonth')], ...(plans.images ? [['imagesPerDay', t('pro.images')]] : [])]
    : []
  const number = (n) => Number(n || 0).toLocaleString()

  return html`
    <div class="panel-hero pro-hero">
      <span class="pro-badge">${Icon.crown(28)}</span>
      <div class="pro-name">Pine AI Pro</div>
      <div class=${cx('small', active ? 'pro-on' : 'dim')}>${status}</div>
    </div>
    ${limits && html`
      <${Section} title=${t('pro.what')}>
        <div class="pro-table">
          <span></span><span class="dim small">${t('pro.plain')}</span><span class="pro-on small">Pro</span>
          ${rows.map(([key, label]) => html`
            <span key=${key}>${label}</span>
            <span class="num dim">${number(limits.free[key])}</span>
            <span class="num pro-on">${number(limits.pro[key])}</span>`)}
        </div>
        ${plans.proModels && plans.proModels.length > 0 && html`
          <${SettingRow} icon=${Icon.bot(18)} title=${t('pro.models')}
            sub=${plans.proModels.map((m) => m.name).join(', ')} />`}
      <//>`}
    <div class="col gap2" style="padding:12px 16px 4px">
      <button class="btn btn-primary" onClick=${() => store.showDialog({
        type: 'contact-dev',
        title: 'Pine AI Pro',
        subject: 'Pine AI Pro',
        lede: t('pro.contactLede'),
        body: t('pro.contactBody')
      })}>${Icon.mail(16)} ${active ? t('pro.extend') : t('pro.get')}</button>
      <div class="hint mute small">${t('pro.howTo')}</div>
    </div>
    <div style="height:16px"></div>`
}

/** The version, and whether this device is connected and how. */
function AboutPage ({ store }) {
  return html`
    <div class="panel-hero" style="padding:20px 16px 8px">
      <span class="tile" style="background:var(--accent);width:56px;height:56px;border-radius:16px;display:grid;place-items:center;color:#fff">
        ${Icon.pine(30)}
      </span>
      <div style="font-weight:600;margin-top:8px">Pine ${appVersion()}</div>
      ${buildDate() && html`<div class="small dim">сборка ${buildDate()}</div>`}
    </div>
    <${Section} title=${t('settings.network')}>
      <${NetworkSection} store=${store} />
    <//>
    <div style="height:16px"></div>`
}

/**
 * Every device signed into this account.
 *
 * Worth being straight about what unlinking does. A linked device holds the
 * account seed — that is what makes it the account rather than a session — so
 * this cannot lock one out the way a server-side session can. It removes the
 * device from the list, and a device running Pine honestly signs itself out
 * when it next looks. For a phone left somewhere that is enough; for one in
 * unfriendly hands, only a new account is.
 */
function DevicesSection ({ store }) {
  const toast = useToast()
  const [devices, setDevices] = useState(null)
  const [busy, setBusy] = useState('')

  const refresh = useCallback(() => {
    store.client.listDevices()
      .then(setDevices)
      .catch(() => setDevices([]))
  }, [store])

  useEffect(() => { refresh() }, [refresh])

  async function revoke (device) {
    setBusy(device.id)
    try {
      await store.client.revokeDevice(device.id)
      toast(t('devices.revoked'))
      refresh()
    } catch (err) {
      toast(err.message || t('err.generic'), { error: true })
    } finally {
      setBusy('')
    }
  }

  if (devices === null) {
    return html`<div class="row center" style="padding:12px"><div class="spinner"></div></div>`
  }

  return html`
    <div>
      ${devices.length === 0
        ? html`<div class="hint mute small" style="padding:0 16px 8px">${t('devices.empty')}</div>`
        : devices.map((device) => html`
          <div key=${device.id} class="list-row" style="cursor:default">
            <span class="dim">${device.platform === 'android' ? Icon.phone(18) : Icon.monitor(18)}</span>
            <span class="body">
              <span class="title">
                ${device.name || t('devices.unnamed')}
                ${device.current && html`<span class="dim small"> · ${t('devices.current')}</span>`}
              </span>
              <span class="sub">${device.at ? listTime(device.at) : ''}</span>
            </span>
            <button class="btn btn-ghost btn-sm" disabled=${busy === device.id}
                    onClick=${() => revoke(device)}>
              ${busy === device.id
                ? html`<div class="spinner"></div>`
                : (device.current ? t('settings.signOut') : t('devices.revoke'))}
            </button>
          </div>`)}

      <${SettingRow} icon=${Icon.qr(18)} title=${t('devices.add')}
        sub=${t('devices.addHint')}
        onClick=${() => store.showDialog({ type: 'link-device' })} />
      <div class="hint mute small" style="padding:0 16px 10px">${t('devices.warning')}</div>
    </div>`
}

/**
 * The profile avatar.
 *
 * An avatar rides inside every member record and every room summary, so it has
 * to stay small: the image is drawn to a square canvas at 256px and re-encoded
 * before it is stored, turning any photo into a few kilobytes of JPEG. That
 * data URI is what travels — no blob store, no separate fetch, it is just part
 * of who you are in a room.
 */
const AVATAR_SIZE = 256
const AVATAR_MAX_BYTES = 96 * 1024

function AvatarPicker ({ client, name }) {
  const toast = useToast()
  const fileRef = useRef(null)
  const [avatar, setAvatar] = useState(client.identity.profile.avatar || null)
  const [busy, setBusy] = useState(false)

  async function pick (event) {
    const file = event.target.files && event.target.files[0]
    event.target.value = '' // let the same file be chosen again after a change
    if (!file) return
    if (!file.type.startsWith('image/')) {
      toast(t('avatar.notImage'), { error: true })
      return
    }
    setBusy(true)
    try {
      const dataUri = await downscale(file)
      if (dataUri.length > AVATAR_MAX_BYTES * 1.4) throw new Error('too big')
      await client.updateProfile({ avatar: dataUri })
      setAvatar(dataUri)
      toast(t('avatar.updated'))
    } catch (err) {
      toast(t('avatar.failed'), { error: true })
    } finally {
      setBusy(false)
    }
  }

  async function remove () {
    setBusy(true)
    try {
      await client.updateProfile({ avatar: null })
      setAvatar(null)
    } finally {
      setBusy(false)
    }
  }

  return html`
    <div class="avatar-picker">
      <${Avatar} name=${name} id=${client.identity.id} src=${avatar} size="xl"
                 onClick=${() => fileRef.current && fileRef.current.click()} />
      <button class="avatar-edit" disabled=${busy}
              onClick=${() => fileRef.current && fileRef.current.click()}
              title=${t('avatar.change')} aria-label=${t('avatar.change')}>
        ${busy ? html`<div class="spinner"></div>` : Icon.camera ? Icon.camera(16) : '✎'}
      </button>
      ${avatar && !busy && html`
        <button class="avatar-remove" onClick=${remove} title=${t('avatar.remove')} aria-label=${t('avatar.remove')}>
          ${Icon.close ? Icon.close(14) : '×'}
        </button>`}
      <input ref=${fileRef} type="file" accept="image/*" hidden onChange=${pick} />
    </div>`
}

/**
 * Draw an image file onto a square canvas, centre-cropped, and hand back a
 * compact JPEG data URI. Runs the same in a browser tab, an Android WebView and
 * an Electron renderer — all three have canvas and createImageBitmap.
 */
async function downscale (file) {
  const bitmap = await createImageBitmap(file)
  const side = Math.min(bitmap.width, bitmap.height)
  const sx = (bitmap.width - side) / 2
  const sy = (bitmap.height - side) / 2

  const canvas = document.createElement('canvas')
  canvas.width = AVATAR_SIZE
  canvas.height = AVATAR_SIZE
  const ctx = canvas.getContext('2d')
  ctx.drawImage(bitmap, sx, sy, side, side, 0, 0, AVATAR_SIZE, AVATAR_SIZE)
  bitmap.close && bitmap.close()

  // Step the quality down until it fits the budget rather than rejecting a
  // large photo outright.
  for (const quality of [0.85, 0.7, 0.55, 0.4]) {
    const uri = canvas.toDataURL('image/jpeg', quality)
    if (uri.length <= AVATAR_MAX_BYTES * 1.4) return uri
  }
  return canvas.toDataURL('image/jpeg', 0.3)
}

/**
 * Which sound an incoming call makes.
 *
 * Pine ships one so a call from Pine is recognisable as a call, and the two
 * other choices hand it back to the phone: its own ringtone, or anything in
 * the system picker. Choosing plays it once — a ringtone you cannot hear
 * before you pick it is a guess.
 */
/**
 * Whether this browser may notify, and a button to ask when it has not been.
 *
 * A browser only shows its permission prompt in answer to a click, so this is
 * that click. Once allowed, the page also subscribes to Web Push — which is
 * what lets a closed tab hear about a call — and asks the browser to keep its
 * storage, which Chrome grants to a site allowed to notify.
 */
/**
 * Starting with the computer. A desktop app cannot be woken from outside the
 * way a phone can, so receiving anything means running — see main.js.
 */
function AutostartRow ({ store }) {
  const [on, setOn] = useState(null)
  useEffect(() => {
    store.platform.autostart.get().then(setOn).catch(() => setOn(false))
  }, [store])
  return html`<${SettingRow} icon=${Icon.monitor(18)} title=${t('settings.autostart')}
    sub=${t('settings.autostartHint')}
    right=${html`<${Switch} checked=${!!on} disabled=${on === null}
      onChange=${async (v) => setOn(await store.platform.autostart.set(v))} />`} />`
}

function BrowserNotificationsRow ({ store }) {
  const toast = useToast()
  const notifications = store.platform.notifications
  const [state, setState] = useState(() => notifications.state())

  if (state === 'unsupported') return null
  if (state === 'install') {
    // The icon starts signed out — iOS gives it storage of its own — so say
    // how to carry the account across before somebody finds out the hard way.
    return html`<${SettingRow} icon=${Icon.bell(18)} title=${t('web.notifyTitle')}
      sub=${t('web.notifyInstall') + ' ' + t('web.installRelogin')} />`
  }

  const right = state === 'granted'
    ? html`<span class="small" style="color:var(--accent)">${t('settings.bgAllowed')}</span>`
    : state === 'denied'
      ? html`<span class="small dim">${t('web.notifyBlocked')}</span>`
      : html`
        <button class="btn btn-sm btn-primary" onClick=${async () => {
          const answer = await notifications.enable()
          setState(answer)
          if (answer === 'granted') toast(t('web.notifyOn'))
        }}>${t('access.allow')}</button>`

  return html`
    <${SettingRow} icon=${Icon.bell(18)} title=${t('web.notifyTitle')} sub=${t('web.notifyHint')} right=${right} />`
}

/**
 * Whether calls go through the phone's own call system.
 *
 * On by default — it is what makes Android treat a Pine call as a call (see
 * CallTelecom.kt in the shell). A switch exists because some manufacturers'
 * builds handle self-managed calls badly, and on those phones the old way —
 * a notification that rings — is the better of two imperfect options. The
 * switch is only shown where there is a choice to make.
 */
function TelecomRow ({ store }) {
  const telecom = store.platform.telecom
  const [status, setStatus] = useState(() => (telecom && telecom.status && telecom.status()) || null)

  if (!status || !status.supported) return null

  return html`
    <${SettingRow} icon=${Icon.phone(18)} title=${t('settings.telecom')}
      sub=${t('settings.telecomHint')}
      right=${html`<${Switch} checked=${!!status.enabled}
        onChange=${(on) => {
          telecom.setEnabled(on)
          setStatus({ ...status, enabled: on })
        }} />`} />`
}

function RingtoneRow ({ store }) {
  const menu = useMenu()
  const ringtone = store.platform.ringtone
  const [value, setValue] = useState(() => ringtone.get())
  const [name, setName] = useState(() => ringtone.name())

  useEffect(() => {
    const onChanged = (event) => {
      const choice = event.detail || {}
      setValue(choice.value || '')
      setName(choice.name || '')
    }
    window.addEventListener('pine:ringtone', onChanged)
    // Nothing should go on playing after this screen is gone.
    return () => {
      window.removeEventListener('pine:ringtone', onChanged)
      ringtone.stopPreview()
    }
  }, [ringtone])

  const choose = (next) => {
    ringtone.set(next)
    setValue(next)
    setName(ringtone.name())
  }

  const label = value === ''
    ? t('settings.ringtonePine')
    : value === 'system'
      ? t('settings.ringtoneSystem')
      : (name || t('settings.ringtonePick'))

  return html`
    <div>
      <${SettingRow} icon=${Icon.bell(18)} title=${t('settings.ringtone')} sub=${label}
        onClick=${(event) => menu.open(event, [
          { label: t('settings.ringtonePine'), onSelect: () => choose('') },
          { label: t('settings.ringtoneSystem'), onSelect: () => choose('system') },
          { separator: true },
          { label: t('settings.ringtonePick'), icon: Icon.more(17), onSelect: () => ringtone.pick() }
        ], 'end')}
        right=${Icon.chevronDown(17)} />
      ${menu.node}
    </div>`
}

/**
 * The three switches that decide whether a call reaches this phone.
 *
 * Notifications, an exemption from battery optimisation, and — on Android 14
 * and later — permission for a call to take over the screen. All three belong
 * to the user, so this can only show what is missing and open the right
 * settings screen. Re-checked whenever the app comes back, because that is
 * exactly when somebody has just granted one.
 */
function BackgroundSection ({ store }) {
  const background = store.platform.background
  const [status, setStatus] = useState(() => background.status())

  useEffect(() => {
    const refresh = () => setStatus(background.status())
    window.addEventListener('focus', refresh)
    document.addEventListener('visibilitychange', refresh)
    return () => {
      window.removeEventListener('focus', refresh)
      document.removeEventListener('visibilitychange', refresh)
    }
  }, [background])

  const rows = [
    ['notifications', t('settings.bgNotifications')],
    ['battery', t('settings.bgBattery')],
    // "Restricted" in the app's battery settings: no service from the
    // background at all, whatever the exemption above says.
    ['unrestricted', t('settings.bgUnrestricted')],
    ['fullScreen', t('settings.bgFullScreen')]
  ]

  return html`
    <div>
      ${rows.map(([key, title]) => {
        // A key the shell does not report is not a problem to show.
        const ok = !status || status[key] !== false
        return html`
          <${SettingRow} key=${key} icon=${ok ? Icon.check(18) : Icon.shield(18)} title=${title}
            right=${ok
              ? html`<span class="small dim">${t('settings.bgAllowed')}</span>`
              : html`
                <button class="btn btn-sm btn-soft"
                        onClick=${() => background.request(key === 'fullScreen' ? 'fullscreen' : key)}>
                  ${t('settings.bgFix')}
                </button>`} />`
      })}
      ${store.platform.push && html`
        <${SettingRow} icon=${Icon.bell(18)} title=${t('settings.push')} sub=${t('settings.pushHint')}
          right=${html`<${Switch} checked=${store.client.settings.push !== false}
            onChange=${(on) => {
              store.client.setSettings({ push: on })
              if (on) store.client.setPushToken(store.platform.push.token()).catch(() => {})
              else store.client.dropPushToken().catch(() => {})
            }} />`} />`}
      ${background.shareJournal && html`
        <${SettingRow} icon=${Icon.file(18)} title=${t('settings.bgJournal')} sub=${t('settings.bgJournalHint')}
          right=${html`
            <button class="btn btn-sm btn-soft" onClick=${() => background.shareJournal()}>
              ${t('settings.bgJournalSend')}
            </button>`} />`}
      <div class="hint mute small" style="padding:0 16px 12px">${t('settings.backgroundHint')}</div>
    </div>`
}

/**
 * The badges this account carries, and which of them to show.
 *
 * Shown here because they are otherwise invisible to their owner: a badge is
 * drawn next to your name in *other* people's apps. Who gets one is not up to
 * the app — that is the operator's call, and the whole point of a badge — but
 * whether to wear it is.
 *
 * Except verification, which stays on. It exists so that other people can tell
 * a real account from somebody imitating it, and a mark you can switch off
 * cannot do that job.
 *
 * The choice lives on the server, because that is where the answer everybody
 * else reads comes from. Reading your own list back is a signed request: what
 * you have been granted is nobody's business but yours, and hiding one has to
 * actually hide it.
 */
function MyBadges ({ client }) {
  const toast = useToast()
  const [state, setState] = useState(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let live = true
    client.myBadges().then((answer) => { if (live) setState(answer) }).catch(() => {})
    return () => { live = false }
  }, [client])

  if (!state || !state.granted.length) return null

  async function toggle (badge, show) {
    const hidden = show
      ? state.hidden.filter((b) => b !== badge)
      : [...new Set([...state.hidden, badge])]

    // Moved at once, put back if the server never hears about it: a switch
    // that waits for a round trip before it moves feels broken.
    const before = state
    setState({ ...state, hidden })
    setBusy(true)
    try {
      const answer = await client.setHiddenBadges(hidden)
      if (answer && Array.isArray(answer.granted) && answer.granted.length) {
        setState(answer)
      } else {
        setState(before)
        // Only a request that never arrived is "the server is unreachable".
        toast(t(answer ? 'settings.badgeRefused' : 'settings.badgeFailed'), { error: true })
      }
    } catch {
      setState(before)
      toast(t('settings.badgeFailed'), { error: true })
    } finally {
      setBusy(false)
    }
  }

  return html`
    <div class="field">
      <label>${t('settings.badges')}</label>
      <div class="col gap2">
        ${state.granted.map((badge) => html`
          <div key=${badge} class="row gap2">
            <span class="badge-chip grow" style="justify-content:flex-start">
              ${Badges({ badges: [badge], size: 15 })}
              <span>${t('badge.' + badge)}</span>
            </span>
            ${badge === 'verified'
              ? html`<span class="small dim">${t('settings.badgeAlways')}</span>`
              : html`
                <${Switch} checked=${!state.hidden.includes(badge)} disabled=${busy}
                  onChange=${(v) => toggle(badge, v)} />`}
          </div>`)}
      </div>
      <span class="hint">${t('settings.badgesHint')}</span>
    </div>`
}

/**
 * Claiming a handle.
 *
 * Nothing is reserved and nothing can fail as "already taken": the name is
 * just a DHT topic this device answers on while it is online. So validation
 * is about the shape of the name, and the status line says whether we are
 * currently announcing it — not whether it is "available".
 */
function UsernameField ({ client }) {
  const toast = useToast()
  const [value, setValue] = useState(client.username || '')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(!!client.username)

  const normalized = normalizeUsername(value)
  const problem = normalized ? usernameError(normalized) : null
  const changed = normalized !== (client.username || '')

  const [rejected, setRejected] = useState(null)

  async function save () {
    setSaving(true)
    setRejected(null)
    try {
      await client.setUsername(normalized)
      setSaved(true)
      toast(t('settings.saved'))
    } catch (err) {
      // 'taken' belongs next to the field, not in a toast that vanishes
      // before the person has retyped.
      const reason = err.reason || 'failed'
      setRejected(reason)
      if (reason !== 'taken') toast(t('username.err.' + reason), { error: true })
    } finally {
      setSaving(false)
    }
  }

  return html`
    <div class="field">
      <label>${t('settings.username')}</label>
      <div class="row gap2">
        <div class="input-prefix grow">
          <span>@</span>
          <input value=${value} maxlength="32" spellcheck="false" autocapitalize="off"
                 placeholder=${t('settings.usernamePlaceholder')}
                 onInput=${(e) => { setValue(e.target.value); setSaved(false); setRejected(null) }}
                 onKeyDown=${(e) => { if (e.key === 'Enter' && !problem && changed) save() }} />
        </div>
        ${changed && html`
          <button class="btn btn-primary" disabled=${saving || !!problem} onClick=${save}>
            ${saving ? html`<div class="spinner"></div>` : t('settings.save')}
          </button>`}
      </div>
      ${problem || rejected
        ? html`<span class="error">${t('username.err.' + (problem || rejected))}</span>`
        : html`
          <span class="hint">
            ${saved && client.username
              ? html`<span style="color:var(--accent)">${t('settings.usernameReserved')}</span>`
              : t('settings.usernameHint')}
          </span>`}
    </div>`
}

function NetworkSection ({ store }) {
  const [peers, setPeers] = useState(store.client.transport.peerCount)

  useEffect(() => {
    const id = setInterval(() => setPeers(store.client.transport.peerCount), 2000)
    return () => clearInterval(id)
  }, [store])

  const status = store.get('status')
  const mode = store.get('mode')

  return html`
    <div>
      <${SettingRow} icon=${Icon.globe(18)} title=${t('settings.connection')}
        right=${html`
          <span class="row gap2">
            <span class=${cx('status-dot', status)}></span>
            <span class="small dim">${t('status.' + status)}</span>
          </span>`} />
      <${SettingRow} icon=${Icon.shield(18)} title=${t('settings.mode')}
        right=${html`<span class="small dim">
          ${mode === 'relay' ? t('settings.modeRelay') : t('settings.modeNative')}
        </span>`} />
      <${SettingRow} icon=${Icon.users(18)} title=${t('settings.peers')}
        right=${html`<span class="small dim">${peers}</span>`} />
    </div>`
}

export function Contacts () {
  const store = useStore()
  const toast = useToast()
  const [contacts, setContacts] = useState([])

  useEffect(() => {
    store.client.listContacts().then(setContacts).catch(() => {})
  }, [store, store.client.requests.length])

  return html`
    <aside class="pane" style="width:100%;max-width:520px">
      <header class="pane-head">
        <h1 class="grow">${t('contacts.title')}</h1>
        <button class="icon-btn" onClick=${() => store.showDialog({ type: 'direct' })}>
          ${Icon.userPlus(20)}
        </button>
      </header>

      <div class="pane-body">
        ${store.client.requests.length > 0 && html`
          <div>
            <div class="section-title">${t('contacts.requests')}</div>
            ${store.client.requests.map((req) => html`
              <div key=${req.invite} class="list-row">
                <${Avatar} name=${req.name} id=${req.from} size="sm" />
                <span class="body">
                  <span class="title">${req.name || shortId(req.from)}</span>
                  <span class="sub">${t('contacts.request')}</span>
                </span>
                <span class="row gap1">
                  <button class="btn btn-primary btn-sm"
                          onClick=${() => store.client.acceptRequest(req).catch((e) => toast(e.message, { error: true }))}>
                    ${t('contacts.accept')}
                  </button>
                  <button class="icon-btn" onClick=${() => store.client.declineRequest(req)}>
                    ${Icon.close(16)}
                  </button>
                </span>
              </div>`)}
          </div>`}

        ${contacts.length === 0
          ? html`
            <div class="empty">
              <div class="glyph">${Icon.contacts(38)}</div>
              <h3>${t('contacts.empty')}</h3>
              <p>${t('contacts.emptyHint')}</p>
              <button class="btn btn-primary" onClick=${() => store.showDialog({ type: 'direct' })}>
                ${Icon.userPlus(17)} ${t('contacts.add')}
              </button>
            </div>`
          : contacts.map((contact) => html`
            <button key=${contact.account} class="list-row"
                    onClick=${() => store.showDialog({ type: 'direct', peer: contact.account, name: contact.name })}>
              <${Avatar} name=${contact.name} id=${contact.account} size="sm" />
              <span class="body">
                <span class="title">${contact.name || shortId(contact.account)}</span>
                <span class="sub mono">${shortId(contact.account)}</span>
              </span>
              ${contact.blocked && html`<span class="chip warn">${t('contacts.blocked')}</span>`}
            </button>`)}
      </div>
    </aside>`
}
